import { isInOfficeHours } from "@btcdirect/functions/src/office-hours";
import { useLogger } from "@btcdirect/shared/src/composables/util/logger";
import { useStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export type PSP = "adyen" | "cm" | "bunq" | "cardGate" | "worldpay";

export interface PaymentMethod {
    code: string;
    label: string;
    limit: number;
    fee: {
        fixed: number;
        percentage: number;
    };
    openInNewTab: boolean;
    disabled?: boolean;
    psp: PSP;
    context?: {
        type: string;
        value: unknown;
    }[] | WorldpayCreditCardContext | ApplePayContext;
}

const { warn } = useLogger();
const DEFAULT_PSP: PSP = "adyen";

export const usePaymentMethodsStore = defineStore("payment-methods", () => {
    const methods = ref<PaymentMethod[]>([]);
    const methodsMapped = ref<Record<string, Lowercase<string>>>({
        mistercash: "bancontact"
    });
    const supportedCardBrands = ref<string[]>(["mc", "visa", "maestro", "cb", "bcmc"]);

    const selected = useStorage<PaymentMethod>("payment-selected", {
        code: isInOfficeHours() ? "bankTransfer" : "creditCard",
        label: null,
        limit: null,
        fee: {
            fixed: null,
            percentage: null
        },
        openInNewTab: null,
        psp: DEFAULT_PSP
    });

    const findPaymentMethod = (paymentMethod: string): PaymentMethod => {
        if (!paymentMethod || !methods.value.length) {
            return null;
        }

        const methodName = paymentMethod.toLowerCase();
        const searchValue = methodsMapped.value[methodName] ?? methodName;

        return methods.value.find((method) => method.code.toLowerCase() === searchValue) ?? null;
    };

    const selectPaymentMethod = (paymentMethod: string): void => {
        if (!paymentMethod) {
            selected.value = null;
            return;
        }

        if (!methods.value || !methods.value.length) {
            warn("Selecting a payment method before payment methods have been retrieved", `Setting a placeholder value for '${paymentMethod}'`);
            selected.value = { code: paymentMethod, label: paymentMethod, limit: null, fee: null, openInNewTab: null, psp: DEFAULT_PSP };
            return;
        }

        const methodName = paymentMethod.toLowerCase();
        const searchValue = methodsMapped.value[methodName] ?? methodName;

        const method = methods.value.find((method) => method.code.toLowerCase() === searchValue);
        if (!method) {
            warn("Could not find provided payment method in the available payment methods, defaulting to credit card");
            selected.value = methods.value.find((method) => method.code == "creditCard");
            return;
        }

        if (!!method.disabled) {
            warn("Trying to select a disabled payment method");
            return;
        }

        selected.value = method;
    };

    const setPaymentMethods = (paymentMethods: PaymentMethod[]): void => {
        if (!paymentMethods || !paymentMethods.length) {
            methods.value = [];
            return;
        }

        methods.value = paymentMethods;

        if (!selected.value) {
            return;
        }

        // Check if the currently selected method is available
        const current = methods.value.find((method) => selected.value.code === method.code);
        if (!!current && !current.disabled) {
            selectPaymentMethod(current.code);
            return;
        }

        selectPaymentMethod(isInOfficeHours() ? "bankTransfer" : "creditCard");
    };

    const getBankTransferLimit = (): number => {
        const method = methods.value.find((method) => method.code === "bankTransfer");
        if (!method) {
            return null;
        }

        return method.limit;
    };

    return {
        paymentMethods: computed<PaymentMethod[]>(() => methods.value),
        selectedPaymentMethod: computed<PaymentMethod>(() => selected.value),
        bankTransferLimit: computed<number>(() => getBankTransferLimit()),
        supportedCardBrands: computed<string[]>(() => supportedCardBrands.value),
        findPaymentMethod,
        selectPaymentMethod,
        setPaymentMethods,
        selectedPSP: computed<PSP>(() => selected.value?.psp ?? DEFAULT_PSP),
    };
});

export type WorldpayCreditCardContext = Array<{
    type: "acceptedCards";
    value: string[];
} | {
    type: "identifier";
    value: string;
}>;

export type ApplePayContext = Array<{
    type: "identifier";
    value: string;
} | {
    type: "acceptedCards";
    value: string[];
} | {
    type: "country";
    value: string;
} | {
    type: "features";
    value: ['supports3DS']
} | {
    type: "displayName";
    value: string;
} | {
    type: "countries";
    value: string[];
}>;